import { Box, Flex, Icon, Stack, StackProps } from "@chakra-ui/react";
import { WarningIcon } from "../../../../shared/views/icons";
import { FacilityType, NumberOfLanesType } from "@keita/lanepatrol-sdk";
import {
  hardSegregationGradient,
  parkingGradient,
  roadLaneColor,
  sidewalkGradient,
} from "./constants";
import {
  CreateOrUpdateHlaLane,
  PresenceDistance,
} from "../../../models/HlaLane";

export const CrossSectionAdjacentFeatures = ({
  lane,
  side,
}: {
  lane: CreateOrUpdateHlaLane;
  side: "left" | "right";
}) => {
  const adjacentFeatures =
    side === "left" ? lane.adjacentFeaturesLeft : lane.adjacentFeaturesRight;

  const moreTheOneLane =
    adjacentFeatures.roadLane &&
    adjacentFeatures.numberOfLanes ===
      NumberOfLanesType.MoreThanOnePerDirection;

  const warningIcon = (distance: PresenceDistance) =>
    (adjacentFeatures.objectOrLevelChange === distance ||
      adjacentFeatures.severeHazard === distance) && (
      <Flex
        bgColor={"white"}
        borderRadius={"full"}
        boxSize={"20px"}
        alignItems={"center"}
        justifyContent={"center"}
        position={"relative"}
      >
        <Icon
          size={"sm"}
          color={
            adjacentFeatures.severeHazard === distance
              ? "red.500"
              : "yellow.500"
          }
          as={WarningIcon}
        />
      </Flex>
    );

  const roadCenterLine = (distance: PresenceDistance) =>
    moreTheOneLane &&
    adjacentFeatures.roadLane === distance && (
      <Box
        position="absolute"
        right={"0"}
        w={mergeWithLane ? "100%" : "calc(50% + 2px)"}
        h={"full"}
        borderLeft={"4px dashed white"}
      />
    );

  const sharedProps: StackProps = {
    width: "40px",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    transitionDuration: "0.2s",
  };

  const sidewalkProps: StackProps = {
    bgSize: "10px 10px",
    bgImage: sidewalkGradient,
  };

  const mergeWithLane =
    lane.facilityType === FacilityType.MixedTrafficRoadLane &&
    adjacentFeatures.roadLane === PresenceDistance.From0to1m;
  const roadLaneProps: StackProps = {
    width: `${(moreTheOneLane && !mergeWithLane ? 2 : 1) * 60}px`,
    bgColor: roadLaneColor,
    bgImage: "none",
  };

  const vehicleParkingProps: StackProps = {
    width: "60px",
    borderLeft: `2px white`,
    borderRight: `2px white`,
    bgColor: "#e5e5f7",
    bgSize: "34px 34px",
    bgImage: parkingGradient,
  };

  const buildProps = (distance: PresenceDistance) => ({
    ...sharedProps,
    ...(adjacentFeatures.sidewalk === distance && sidewalkProps),
    ...(adjacentFeatures.roadLane === distance && roadLaneProps),
    ...(adjacentFeatures.vehicleParking === distance && vehicleParkingProps),
  });

  return (
    <Flex
      alignItems={"stretch"}
      flexDirection={side === "left" ? "row-reverse" : "row"}
    >
      <Box
        w={adjacentFeatures.hardSegregation ? "8px" : "0px"}
        transitionDuration={"0.1s"}
        bgColor="#e5e5f7"
        opacity={0.8}
        background={hardSegregationGradient}
      />

      <Stack {...buildProps(PresenceDistance.From0to1m)}>
        {roadCenterLine(PresenceDistance.From0to1m)}
        {warningIcon(PresenceDistance.From0to1m)}
      </Stack>

      <Stack {...buildProps(PresenceDistance.From1to3m)}>
        {roadCenterLine(PresenceDistance.From1to3m)}
        {warningIcon(PresenceDistance.From1to3m)}
      </Stack>
    </Flex>
  );
};
